import {
  MfLatestSnapshot,
  MfHistoryPayload,
  MfMonthlyPayload,
  MfMonthlyItem,
  MfYearlyTotal,
} from '../types';
import { API_CONFIG } from '../constants';
import { SEED_MF_MONTHLY_DATA, SEED_MF_DAILY_RECORDS } from './mutualFundSeedData';

const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes cache

let latestCache: { data: MfLatestSnapshot; timestamp: number } | null = null;
let historyCache: { data: MfHistoryPayload; timestamp: number } | null = null;
let monthlyCache: { data: MfMonthlyPayload; timestamp: number } | null = null;

let pendingLatestFetch: Promise<MfLatestSnapshot> | null = null;
let pendingHistoryFetch: Promise<MfHistoryPayload> | null = null;
let pendingMonthlyFetch: Promise<MfMonthlyPayload> | null = null;

/**
 * Formats a number in ₹ Crores with Indian localized number formatting and optional sign.
 */
export const formatCr = (value: number | undefined | null, includeSign = false): string => {
  if (value === undefined || value === null || isNaN(value)) return '₹0 Cr';
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const sign = value > 0 ? (includeSign ? '+' : '') : value < 0 ? '-' : '';
  return `${sign}₹${formatted} Cr`;
};

/**
 * Formats AUM in ₹ Lakh Crore with Indian numbering formatting.
 */
export const formatLakhCr = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '₹0L Cr';
  return `₹${value.toFixed(2)}L Cr`;
};

/**
 * Builds fallback default yearly totals based on monthly items.
 */
export function calculateYearlyTotals(monthlyData: MfMonthlyItem[]): MfYearlyTotal[] {
  const grouped = monthlyData.reduce<Record<number, MfMonthlyItem[]>>((acc, item) => {
    acc[item.year] = acc[item.year] || [];
    acc[item.year].push(item);
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([yearStr, items]) => {
      const year = parseInt(yearStr, 10);
      const totalSip = items.reduce((sum, i) => sum + i.sipInflowCr, 0);
      const totalEquity = items.reduce((sum, i) => sum + i.netEquityInflowCr, 0);
      const lastItem = items[items.length - 1];

      return {
        year,
        totalSipInflowCr: Math.round(totalSip),
        totalEquityInflowCr: Math.round(totalEquity),
        avgMonthlySipCr: Math.round(totalSip / items.length),
        endingAumLakhCr: lastItem ? lastItem.totalIndustryAumLakhCr : 0,
        months: items.length,
      };
    })
    .sort((a, b) => b.year - a.year);
}

/**
 * Generates fallback latest snapshot from seed dataset.
 */
export function getFallbackLatestSnapshot(): MfLatestSnapshot {
  const latestDaily = SEED_MF_DAILY_RECORDS[0];
  const latestMonth = SEED_MF_MONTHLY_DATA[SEED_MF_MONTHLY_DATA.length - 1];

  const rolling5d = SEED_MF_DAILY_RECORDS.slice(0, 5).reduce((sum, r) => sum + r.equityNet, 0);
  const rolling30d = SEED_MF_DAILY_RECORDS.slice(0, 30).reduce((sum, r) => sum + r.equityNet, 0);

  const diiEq = latestDaily.diiEquityNet ?? latestDaily.equityNet * 0.78;
  const sharePct = diiEq !== 0 ? Math.min(100, Math.round((latestDaily.equityNet / diiEq) * 100)) : 75;

  return {
    date: latestDaily.date,
    displayDate: latestDaily.date,
    updatedAt: new Date().toISOString(),
    equityBuy: latestDaily.equityBuy,
    equitySell: latestDaily.equitySell,
    equityNet: latestDaily.equityNet,
    debtBuy: latestDaily.debtBuy,
    debtSell: latestDaily.debtSell,
    debtNet: latestDaily.debtNet,
    totalNet: latestDaily.totalNet,
    diiEquityNet: diiEq,
    mfShareOfDiiPct: sharePct,
    latestMonthSipCr: latestMonth.sipInflowCr,
    latestMonthEquityInflowCr: latestMonth.netEquityInflowCr,
    latestIndustryAumLakhCr: latestMonth.totalIndustryAumLakhCr,
    rolling5dEquityNet: Math.round(rolling5d),
    rolling30dEquityNet: Math.round(rolling30d),
  };
}

/**
 * Fetches the latest session Mutual Fund snapshot.
 */
export async function fetchMfFlowLatest(forceRefresh = false): Promise<MfLatestSnapshot> {
  const now = Date.now();
  if (!forceRefresh && latestCache && now - latestCache.timestamp < CACHE_TTL_MS) {
    return latestCache.data;
  }

  if (pendingLatestFetch && !forceRefresh) {
    return pendingLatestFetch;
  }

  pendingLatestFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MF_FLOW_LATEST}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Endpoint returned ${response.status}`);
      }
      const data: MfLatestSnapshot = await response.json();
      latestCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      // Return high-fidelity fallback seed snapshot
      const fallback = getFallbackLatestSnapshot();
      latestCache = { data: fallback, timestamp: Date.now() };
      return fallback;
    } finally {
      pendingLatestFetch = null;
    }
  })();

  return pendingLatestFetch;
}

/**
 * Fetches daily time-series records of SEBI Mutual Fund Equity & Debt flows.
 */
export async function fetchMfFlowHistory(forceRefresh = false): Promise<MfHistoryPayload> {
  const now = Date.now();
  if (!forceRefresh && historyCache && now - historyCache.timestamp < CACHE_TTL_MS) {
    return historyCache.data;
  }

  if (pendingHistoryFetch && !forceRefresh) {
    return pendingHistoryFetch;
  }

  pendingHistoryFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MF_FLOW_HISTORY}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Endpoint returned ${response.status}`);
      }
      const data: MfHistoryPayload = await response.json();
      historyCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      const fallback: MfHistoryPayload = {
        generatedAt: new Date().toISOString(),
        totalDays: SEED_MF_DAILY_RECORDS.length,
        records: SEED_MF_DAILY_RECORDS,
      };
      historyCache = { data: fallback, timestamp: Date.now() };
      return fallback;
    } finally {
      pendingHistoryFetch = null;
    }
  })();

  return pendingHistoryFetch;
}

/**
 * Fetches 5+ year AMFI Monthly Inflow & SIP trajectory dataset (2020-2026).
 */
export async function fetchMfFlowMonthly(forceRefresh = false): Promise<MfMonthlyPayload> {
  const now = Date.now();
  if (!forceRefresh && monthlyCache && now - monthlyCache.timestamp < CACHE_TTL_MS) {
    return monthlyCache.data;
  }

  if (pendingMonthlyFetch && !forceRefresh) {
    return pendingMonthlyFetch;
  }

  pendingMonthlyFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MF_FLOW_MONTHLY}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Endpoint returned ${response.status}`);
      }
      const data: MfMonthlyPayload = await response.json();
      monthlyCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      const fallback: MfMonthlyPayload = {
        generatedAt: new Date().toISOString(),
        range: '2020-2026',
        totalMonths: SEED_MF_MONTHLY_DATA.length,
        monthlyData: SEED_MF_MONTHLY_DATA,
        yearlyTotals: calculateYearlyTotals(SEED_MF_MONTHLY_DATA),
      };
      monthlyCache = { data: fallback, timestamp: Date.now() };
      return fallback;
    } finally {
      pendingMonthlyFetch = null;
    }
  })();

  return pendingMonthlyFetch;
}
